import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { getTemplateDef } from "@/lib/emailTemplates";
import { useEmailTemplateRows, EmailTemplateRow } from "@/hooks/useEmailTemplate";

export interface EmailRecipient {
  id: string;
  full_name: string;
  preferred_name?: string | null;
  email: string | null;
  /** Personal one-time correction link, inserted wherever {{link}} appears. */
  fixUrl?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leadId: string;
  recipients: EmailRecipient[];
  intakeUrl?: string;
  initialTemplate?: TemplateKey;
  /** Called with the submission ids that were emailed successfully. */
  onSent?: (recipientIds: string[]) => void;
}

export type TemplateKey = "blank" | "correct" | "passport" | "correction";

const TEMPLATE_KEY_MAP: Record<TemplateKey, string> = {
  blank: "intake_blank",
  correct: "intake_correct",
  passport: "intake_passport",
  correction: "intake_correction",
};

const TEMPLATE_LABELS: Record<TemplateKey, string> = {
  blank: "Blank",
  correct: "Please correct your submission",
  passport: "Passport scan needed",
  correction: "Correction link",
};

/**
 * Wording comes from the admin-editable Email Templates library
 * (Admin → Email Templates), falling back to the built-in defaults.
 */
function buildTemplates(
  rows: EmailTemplateRow[] | undefined,
  intakeUrl: string,
): Record<TemplateKey, { label: string; subject: string; body: string }> {
  const out = {} as Record<TemplateKey, { label: string; subject: string; body: string }>;
  (Object.keys(TEMPLATE_KEY_MAP) as TemplateKey[]).forEach((k) => {
    const key = TEMPLATE_KEY_MAP[k];
    const def = getTemplateDef(key);
    const row = rows?.find((r) => r.template_key === key && r.brand_id === null && r.is_active);
    let body = row?.body?.trim() ? row.body : def?.body ?? "";
    if (k === "blank") {
      // The blank starter uses the shared intake form link, not a personal fix link.
      body = body.replace(/\{\{link\}\}/g, intakeUrl ? `Your intake form:\n${intakeUrl}` : "");
    }
    out[k] = {
      label: TEMPLATE_LABELS[k],
      subject: row?.subject !== undefined && row.subject !== null ? row.subject : def?.subject ?? "",
      body,
    };
  });
  return out;
}


function nameFor(r: EmailRecipient) {
  return (r.preferred_name || r.full_name || "").trim() || "there";
}

function fill(text: string, r: EmailRecipient) {
  return text
    .replace(/\{\{name\}\}/g, nameFor(r))
    .replace(/\{\{link\}\}/g, r.fixUrl || "");
}


function escapeHtml(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function toHtml(text: string) {
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:#111">${escapeHtml(text)
    .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1">$1</a>')
    .replace(/\n/g, "<br/>")}</div>`;
}

export function EmailStudentsDialog({ open, onOpenChange, leadId, recipients, intakeUrl = "", initialTemplate = "blank", onSent }: Props) {
  const { data: templateRows } = useEmailTemplateRows(open);
  const tpl = useMemo(() => buildTemplates(templateRows, intakeUrl), [templateRows, intakeUrl]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<{ name: string; ok: boolean; error?: string }[] | null>(null);

  const valid = recipients.filter((r) => !!r.email?.trim());
  const invalid = recipients.filter((r) => !r.email?.trim());
  const usesLink = /\{\{link\}\}/.test(subject) || /\{\{link\}\}/.test(body);
  const missingLink = usesLink ? valid.filter((r) => !r.fixUrl) : [];


  useEffect(() => {
    if (!open) return;
    setResults(null);
    const t = tpl[initialTemplate];
    setSubject(t.subject);
    setBody(t.body);
  }, [open, initialTemplate, tpl]);

  function applyTemplate(key: TemplateKey) {
    setSubject(tpl[key].subject);
    setBody(tpl[key].body);
  }

  async function send() {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and a message"); return; }
    if (valid.length === 0) { toast.error("No recipients with an email address"); return; }
    if (usesLink && missingLink.length > 0) {
      toast.error(`No correction link yet for ${missingLink.map((r) => r.full_name).join(", ")} — use "Request correction" first, or remove {{link}} from the message.`);
      return;
    }
    setSending(true);
    setResults(null);
    const out: { name: string; ok: boolean; error?: string }[] = [];
    const sentIds: string[] = [];
    for (const r of valid) {
      try {
        const { data, error } = await supabase.functions.invoke("send-lead-email", {
          body: {
            leadId,
            to: [r.email!.trim()],
            subject: fill(subject, r),
            bodyText: fill(body, r),
            bodyHtml: toHtml(fill(body, r)),
          },
        });
        if (error) throw new Error(error.message);

        if ((data as any)?.error) throw new Error((data as any).error);
        out.push({ name: r.full_name, ok: true });
        sentIds.push(r.id);
      } catch (e: any) {
        out.push({ name: r.full_name, ok: false, error: e?.message || "Failed" });
      }
    }
    setResults(out);
    setSending(false);
    if (sentIds.length) onSent?.(sentIds);
    const okCount = out.filter((o) => o.ok).length;
    if (okCount === out.length) toast.success(`Sent to ${okCount} recipient${okCount === 1 ? "" : "s"}`);
    else toast.error(`${okCount}/${out.length} sent — see details`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email students</DialogTitle>
          <DialogDescription>
            Each recipient gets their own separate email — addresses are never shared between them. Every message is logged in this lead's email thread.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Recipients ({valid.length})
            </Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {valid.map((r) => (
                <Badge key={r.id} variant="secondary" className="font-normal">{r.full_name}</Badge>
              ))}
              {valid.length === 0 && <p className="text-sm text-muted-foreground">No recipients with an email address.</p>}
            </div>
            {invalid.length > 0 && (
              <p className="text-xs text-destructive mt-1">
                Skipped (no email): {invalid.map((r) => r.full_name).join(", ")}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {(Object.keys(tpl) as TemplateKey[]).map((k) => (
              <Button key={k} type="button" variant="outline" size="sm" onClick={() => applyTemplate(k)}>
                {tpl[k].label}
              </Button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="es-subject">Subject</Label>
            <Input id="es-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="es-body">Message</Label>
            <Textarea id="es-body" value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="resize-y" />
            <p className="text-xs text-muted-foreground">
              Use <code>{"{{name}}"}</code> for each student's name and <code>{"{{link}}"}</code> for their personal
              correction link — that link only asks for the items you ticked, never the whole form.
            </p>
          </div>

          {usesLink && missingLink.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                No correction link exists yet for {missingLink.map((r) => r.full_name).join(", ")}. Close this and use
                <strong> Request correction</strong> to pick exactly what they must redo — then the email is one click.
              </AlertDescription>
            </Alert>
          )}


          {results && (
            <Alert variant={results.every((r) => r.ok) ? "default" : "destructive"}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {results.filter((r) => r.ok).length} of {results.length} sent.
                {results.some((r) => !r.ok) && (
                  <span> Failed: {results.filter((r) => !r.ok).map((r) => `${r.name} (${r.error})`).join("; ")}</span>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Close</Button>
          <Button onClick={send} disabled={sending || valid.length === 0 || missingLink.length > 0}>
            {sending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Send to {valid.length} recipient{valid.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
